import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useCheckoutService } from '~~/layers/base/services/checkout.service'
import { useAddressService } from '~~/layers/base/services/address.service'
import type {
    CheckoutAddressesData,
    CheckoutCartAddress,
    CheckoutContextInput,
    CheckoutCouponData,
    CheckoutEntitlementsData,
    CheckoutJourney,
    CheckoutHydrateResult,
    CheckoutLoadingPhase,
    CheckoutLoadingScope,
    CheckoutOrderSubmitResult,
    CheckoutPipelineAuthContext,
    CheckoutReturnAddressSyncResult,
    CheckoutSettlement,
    CheckoutShippingData,
    CheckoutSegment,
    CheckoutUiMode,
    CheckoutSubmitResult,
    CheckoutTotalsData,
    CheckoutTransitionResult,
    HydrateCheckoutPageOptions,
    PersistStripeBillingAddressResult,
    PlaceOrderInput,
} from '~~/layers/base/types/checkout'
import {
    CHECKOUT_USER_ERROR_MESSAGES,
    toCheckoutUserMessage,
} from '~~/layers/base/utils/checkoutErrorMessages'
import {
    deserializeCheckForCreditsResponse,
    deserializeFinalizeCartResponse,
    deserializeGetPaymentIntentResponse,
    deserializePlaceOrderResponse,
    deserializePrepareCartResponse,
    addressesFromFinalizeCart,
    addressesFromPrepareCart,
    deserializeSyncReturnAddressResponse,
    entitlementsFromCheckForCredits,
    shippingFromFinalizeCart,
    skuFromFinalizeCart,
    totalsFromFinalizeCart,
} from '~~/layers/base/utils/checkoutApiBoundary'
import { createCheckoutLoadingPhaseController } from '~~/layers/base/utils/checkoutLoadingPhase'
import { persistStripeBillingAddress as runPersistStripeBillingAddress } from '~~/layers/base/utils/persistStripeBillingAddress'
import {
    createIdleSegment,
    createLoadingSegment,
    DEFAULT_CHECKOUT_SETTLEMENT,
    deriveCheckoutSettlement,
    resolveCheckoutUiMode,
    resetSegment,
    setSegmentError,
    setSegmentLoading,
    setSegmentReady,
    shouldPivotToAsyncCredit,
    shouldPivotToPaidCheckout,
    shouldPreWarmOnLoad,
    shouldRunPlaceOrder,
} from '~~/layers/base/utils/checkoutSegment'
import {
    resolveOptimisticCheckoutAddresses,
    resolveOptimisticCheckoutShipping,
} from '~~/layers/base/utils/optimisticCheckoutPreview'
import { clearShoppingSessionPersist } from '~~/layers/base/utils/shoppingSessionPersist'

const CART_SKELETON = { cartId: null as string | null }
const ENTITLEMENTS_SKELETON: CheckoutEntitlementsData = {
    hasNoteCredit: false,
    hasStationeryVoucher: false,
    isProUser: false,
    stationeryVouchersGranted: null,
    stationeryVouchersAvailable: null,
    noteCreditsRemaining: null,
    noteCreditsGranted: null,
    noteCreditsConsumed: null,
    proTier: null,
    voucherUnavailableReason: null,
    subscriptionExpiresAt: null,
}
const TOTALS_SKELETON: CheckoutTotalsData = {
    grandTotal: null,
    subtotal: null,
    taxAmount: null,
    currency: null,
}
const SHIPPING_SKELETON: CheckoutShippingData = {
    selectedMethodCode: null,
    methods: [],
}
const ADDRESSES_SKELETON: CheckoutAddressesData = {
    shippingAddress: null,
    billingAddress: null,
}
const COUPON_SKELETON: CheckoutCouponData = {
    code: null,
    applied: false,
}

/** Guest paid path: defer prepare-cart until checkout UI supplies email (e.g. Stripe). */
function isAwaitingGuestCheckoutEmail(
    isLoggedIn: boolean,
    email: string | null | undefined,
    cartId: string | null | undefined
): boolean {
    return !isLoggedIn && !email?.trim() && !cartId?.trim()
}

function prepareCartOverrides(
    isLoggedIn: boolean,
    email?: string | null
): Partial<CheckoutContextInput> {
    if (isLoggedIn || !email?.trim()) {
        return {}
    }
    return { email: email.trim() }
}

/** Arguments for {@link bindJourneyContext}. Prefer resolving via `resolveCheckoutJourneyBinding`. */
export type BindJourneyContextParams = {
    /** Resolved product journey — set by `resolveCheckoutJourneyBinding`, not by pages directly. */
    journey: CheckoutJourney
    designProjectId?: string | null
    handwritingProjectId?: string | null
    handwritingStationeryId?: string | null
    /** Set after place or when resuming checkout. */
    shoppingSessionId?: string | null
}

/**
 * Global shopping session + checkout orchestration store (TK-11709).
 *
 * Session refs (`journey`, project IDs, …) are set via `bindJourneyContext`.
 * Segments (`cart`, `entitlements`, `totals`, `shipping`, `addresses`, `coupon`) drive skeleton → live UI.
 *
 * **Casing:** segment `data` and refs stay camelCase. `buildContextInput()` merges refs for the
 * service layer; `checkoutApiBoundary` deserializes wire responses before updating segments — do not
 * read `cart_id` / `has_note_credit` etc. directly in this file.
 *
 * Phased pipeline (pass `useBetterAuth().isLoggedIn` from pages):
 * 1. Personalize Send → `seedOptimisticCheckoutPreview()` then navigate (address cards ready)
 * 2. `beginCheckoutTransition({ isLoggedIn })` — credits; signed-in may background prepare-cart if none yet
 * 3. `hydrateCheckoutPage({ isLoggedIn, email? })` — re-seed backstop; prepare double-checks addresses;
 *    paid/voucher finalize in background (TK-12688)
 * 4. `submitOrder` — checkout Submit (sync place on paid path)
 *
 * **Addresses** and **entitlements** start as `loading` (not `idle`) so Recipient / Return and
 * DeliveryHero / CTA never paint “ready” then flash skeletons when prepare or check-for-credits runs.
 * Optimistic address seed / prepare / finalize / sessionStorage restore flip addresses to `ready`;
 * check-for-credits flips entitlements to `ready`.
 *
 * Address sync for an existing cart happens on personalize (`refreshCartAddresses` after recipient/return
 * save), not again at checkout transition — finalize still re-applies as a backstop.
 *
 * **Reload:** `plugins/shopping-session-persist.client.ts` keeps journey + attempt IDs and ready
 * **addresses** in `sessionStorage` so personalize and `/checkout` refresh reuse the same Pinia
 * attempt (no IDs in the checkout URL). Cleared via `resetCheckout` after place success (page)
 * or abandon/logout.
 *
 * @see `doc/guide/GUIDE_handwriting_checkout_api_inventory.md`
 */
export const useShoppingSessionStore = defineStore('shoppingSession', () => {
    const {
        prepareCart: prepareCartApi,
        checkForCredits: checkForCreditsApi,
        finalizeCart: finalizeCartApi,
        placeOrder: placeOrderApi,
        applyCoupon: applyCouponApi,
        removeCoupon: removeCouponApi,
        syncReturnAddress: syncReturnAddressApi,
        getPaymentIntent: getPaymentIntentApi,
    } = useCheckoutService()

    const designProjectId = ref<string | null>(null)
    const handwritingProjectId = ref<string | null>(null)
    const handwritingStationeryId = ref<string | null>(null)
    const journey = ref<CheckoutJourney | null>(null)
    const shoppingSessionId = ref<string | null>(null)
    /**
     * Paid | voucher | async_credit — recomputed after `checkCredits` when entitlements are ready.
     * Defaults to {@link DEFAULT_CHECKOUT_SETTLEMENT} (`async_credit`) before credits resolve;
     * that is a pre-check placeholder for credit-shaped UI, not proof the user has credits.
     */
    const checkoutSettlement = ref<CheckoutSettlement>(DEFAULT_CHECKOUT_SETTLEMENT)
    /**
     * Live completeness of the Stripe billing Address Element / Payment Element on paid checkout
     * (TK-12183) — set from each element's `change` event, read by `resolveCheckoutSubmitGuard` so
     * submit stays disabled until both are filled in. Not persisted; irrelevant off the paid path.
     */
    const stripeBillingAddressComplete = ref(false)
    const stripePaymentComplete = ref(false)
    /**
     * Client secret from the confirmed PaymentIntent (TK-12183 / TK-12803) — set once
     * `stripe.confirmPayment` resolves without error. Persisted in `sessionStorage` reload
     * recovery so in-tab remount can skip a second confirm; cleared by `resetCheckout`.
     */
    const stripeClientSecret = ref<string | null>(null)
    /**
     * Set when submit re-check drops async credit → paid. Checkout page watches this so the UI
     * pivot does not depend on `CheckoutCreditShell` still being mounted (Vue may remount the
     * paid shell as soon as `checkoutSettlement` flips during `await checkCredits()`).
     */
    const pendingPaidCheckoutPivot = ref(false)
    /**
     * Set when `submitOrder` succeeds. Checkout page watches this so success navigation does not
     * depend on the credit/paid shell still being mounted (`checkCredits` can flip entitlements to
     * loading and unmount the shell before `emit('submitted')` runs).
     */
    const pendingCheckoutSuccessNavigation = ref(false)
    /**
     * SKU from finalize-cart line items — passed on place-cart for async_credit preflight.
     */
    const placeSku = ref<string | null>(null)
    /** Guards `preWarmCart` so optional load-time prepare runs at most once per session. */
    const preWarmAttempted = ref(false)
    /** Guards `preWarmEntitlements` so optional load-time credits check runs at most once per session. */
    const preWarmCreditsAttempted = ref(false)

    /** UX escalation phase for the active checkout operation (see `useCheckoutLoadingPhase`). */
    const loadingPhase = ref<CheckoutLoadingPhase>('idle')
    /** Which pipeline step owns the active loading timer. */
    const activeLoadingScope = ref<CheckoutLoadingScope | null>(null)

    let loadingPhaseController: ReturnType<typeof createCheckoutLoadingPhaseController> | null =
        null

    /**
     * In-flight finalize from optimistic hydrate — submit awaits this so place does not race.
     * Cleared when the promise settles or on `resetCheckout`.
     */
    let finalizeInFlight: Promise<void> | null = null
    /**
     * In-flight prepare-cart call. Transition and hydrate can both warm the cart; coalesce them so
     * one checkout attempt does not create competing ACCS carts.
     */
    let cartReadyInFlight: Promise<string> | null = null
    /** Bumped on reset so a stale background finalize cannot rewrite cleared segments. */
    let checkoutGeneration = 0

    /** ACCS guest cart id (`shopping_session.accs_quote_id` on the server). */
    const cart = ref<CheckoutSegment<typeof CART_SKELETON>>(createIdleSegment(CART_SKELETON))
    const entitlements = ref<CheckoutSegment<CheckoutEntitlementsData>>(
        createLoadingSegment(ENTITLEMENTS_SKELETON)
    )
    const totals = ref<CheckoutSegment<CheckoutTotalsData>>(createIdleSegment(TOTALS_SKELETON))
    const shipping = ref<CheckoutSegment<CheckoutShippingData>>(
        createIdleSegment(SHIPPING_SKELETON)
    )
    /**
     * Addresses default to `loading` (not `idle`) so Recipient / Return skeletons show on
     * first paint. Send → checkout should call {@link seedOptimisticCheckoutPreview} before
     * navigate so this flips to `ready` with personalize data; prepare/finalize still refresh.
     */
    const addresses = ref<CheckoutSegment<CheckoutAddressesData>>(
        createLoadingSegment(ADDRESSES_SKELETON)
    )
    const coupon = ref<CheckoutSegment<CheckoutCouponData>>(createIdleSegment(COUPON_SKELETON))

    const cartId = computed(() => cart.value.data.cartId)

    const isCartSegmentLoading = computed(() => cart.value.status === 'loading')

    const isOrchestrationEnabled = computed(() => journey.value !== null)

    const isCheckoutSlow = computed(
        () =>
            loadingPhase.value === 'slow' ||
            loadingPhase.value === 'engaged' ||
            loadingPhase.value === 'critical'
    )

    /** True at ~8s+ — UI may show tips/gimmick content (Crane facts, etc.). */
    const showCheckoutEngagedContent = computed(
        () => loadingPhase.value === 'engaged' || loadingPhase.value === 'critical'
    )

    /** Checkout UI shell from bound `journey` + `checkoutSettlement` (see `resolveCheckoutUiMode`). */
    const checkoutMode = computed((): CheckoutUiMode | null =>
        resolveCheckoutUiMode(journey.value, checkoutSettlement.value)
    )

    function startLoadingScope(scope: CheckoutLoadingScope): void {
        stopLoadingScope()
        activeLoadingScope.value = scope
        loadingPhaseController = createCheckoutLoadingPhaseController((phase) => {
            if (activeLoadingScope.value === scope) {
                loadingPhase.value = phase
            }
        })
        loadingPhaseController.start()
    }

    function stopLoadingScope(): void {
        loadingPhaseController?.stop()
        loadingPhaseController = null
        activeLoadingScope.value = null
        loadingPhase.value = 'idle'
    }

    async function withLoadingScope<T>(
        scope: CheckoutLoadingScope,
        fn: () => Promise<T>
    ): Promise<T> {
        startLoadingScope(scope)
        try {
            return await fn()
        } finally {
            stopLoadingScope()
        }
    }

    function assertJourneyContextBound(): void {
        if (!journey.value) {
            throw new Error('Checkout journey context is not bound')
        }
    }

    /**
     * Merges session refs into camelCase context for checkout routes.
     *
     * Serialized in `useCheckoutService` — this store never constructs snake_case POST bodies.
     * Never includes `profileId` (server reads session cookie).
     */
    function buildContextInput(
        overrides: Partial<CheckoutContextInput> = {}
    ): CheckoutContextInput {
        return {
            cartId: cart.value.data.cartId,
            designProjectId: designProjectId.value,
            handwritingProjectId: handwritingProjectId.value,
            handwritingStationeryId: handwritingStationeryId.value,
            journey: journey.value,
            shoppingSessionId: shoppingSessionId.value,
            ...(placeSku.value?.trim() ? { sku: placeSku.value.trim() } : {}),
            ...overrides,
        }
    }

    /** Marks ACCS cart id on the `cart` segment after prepare/finalize APIs succeed. */
    function persistCartId(id: string): void {
        const trimmed = id.trim()
        if (!trimmed) {
            return
        }
        cart.value = setSegmentReady(cart.value, { cartId: trimmed })
    }

    /** Keep prepare/finalize session id so later calls reuse the same shopping_session row. */
    function persistShoppingSessionId(id: string | null | undefined): void {
        const trimmed = id?.trim()
        if (!trimmed) {
            return
        }
        shoppingSessionId.value = trimmed
    }

    /** Apply prepare-cart response: cart id, shopping session id, and DB addresses when present. */
    function persistPrepareCartResult(prepared: {
        cartId?: string | null
        shoppingSessionId?: string | null
        shippingAddress?: CheckoutAddressesData['shippingAddress']
        billingAddress?: CheckoutAddressesData['billingAddress']
    }): string | null {
        const nextCartId = prepared.cartId?.trim() || null
        if (nextCartId) {
            persistCartId(nextCartId)
        }
        persistShoppingSessionId(prepared.shoppingSessionId)
        const preparedAddresses = addressesFromPrepareCart(prepared)
        if (preparedAddresses) {
            addresses.value = setSegmentReady(addresses.value, preparedAddresses)
        }
        return nextCartId
    }

    function recomputeCheckoutSettlement(): void {
        checkoutSettlement.value = deriveCheckoutSettlement(journey.value, entitlements.value.data)
    }

    function resolveAsyncCreditPivot(
        entitlementSnapshot: CheckoutEntitlementsData
    ): CheckoutTransitionResult | null {
        if (!journey.value || !shouldPivotToAsyncCredit(journey.value, entitlementSnapshot)) {
            return null
        }
        checkoutSettlement.value = 'async_credit'
        return { ok: false, pivoted: 'async_credit' }
    }

    function resolvePaidCheckoutPivot(
        wasCreditCheckout: boolean,
        options?: { paymentMethodCode?: string }
    ): CheckoutOrderSubmitResult | null {
        if (!wasCreditCheckout) {
            return null
        }
        if (
            !shouldPivotToPaidCheckout(
                journey.value,
                checkoutSettlement.value,
                Boolean(options?.paymentMethodCode?.trim())
            )
        ) {
            return null
        }
        pendingPaidCheckoutPivot.value = true
        return { ok: false, pivoted: 'paid' }
    }

    function clearPendingPaidCheckoutPivot(): void {
        pendingPaidCheckoutPivot.value = false
    }

    function clearPendingCheckoutSuccessNavigation(): void {
        pendingCheckoutSuccessNavigation.value = false
    }

    /**
     * Register the active product journey and related entity IDs for checkout API payloads.
     *
     * Sets `journey` plus optional `designProjectId`, `handwritingProjectId`,
     * `handwritingStationeryId`, and `shoppingSessionId`.
     *
     * When journey or entity IDs change (new project / abandon prior attempt), wipes segments and
     * `sessionStorage` via {@link resetCheckout} first so stale Recipient / Return cards cannot
     * follow the new bind (TK-12805). Same-context re-bind after reload keeps restored attempt.
     *
     * Call from `useCheckoutPreWarm` (resolves journey via `resolveCheckoutJourneyBinding`).
     * Does not call the network — only stores refs for `buildContextInput()`. Recomputes
     * `checkoutSettlement` only when entitlements are already `ready`; otherwise leaves the
     * {@link DEFAULT_CHECKOUT_SETTLEMENT} placeholder until `checkCredits`.
     */
    function bindJourneyContext(params: BindJourneyContextParams): void {
        const nextDesignProjectId = params.designProjectId ?? null
        const nextHandwritingProjectId = params.handwritingProjectId ?? null
        const nextHandwritingStationeryId = params.handwritingStationeryId ?? null
        // Same URL re-bind after refresh: keep restored shoppingSessionId / cart / addresses.
        const keepAttempt =
            journey.value === params.journey &&
            designProjectId.value === nextDesignProjectId &&
            handwritingProjectId.value === nextHandwritingProjectId &&
            handwritingStationeryId.value === nextHandwritingStationeryId

        if (!keepAttempt) {
            resetCheckout()
        }

        journey.value = params.journey
        designProjectId.value = nextDesignProjectId
        handwritingProjectId.value = nextHandwritingProjectId
        handwritingStationeryId.value = nextHandwritingStationeryId
        if (params.shoppingSessionId != null && params.shoppingSessionId !== '') {
            shoppingSessionId.value = params.shoppingSessionId
        } else if (!keepAttempt) {
            shoppingSessionId.value = null
        }
        // Skeleton entitlements would force `paid` via derive — wait for checkCredits.
        if (entitlements.value.status === 'ready') {
            recomputeCheckoutSettlement()
        }
    }

    /**
     * Fire-and-forget `POST /api/checkout/prepare-cart` on page load when a journey is bound
     * and {@link shouldPreWarmOnLoad} allows it (checkout-API journeys only — not ecommerce
     * Magento/ACCS designer yet; TK-13531).
     *
     * Failures are **silent** (cart segment returns to `idle`) so personalize is not blocked.
     * Guest vs signed-in only changes server pre-fill inside `prepareGuestCart`, not this call.
     */
    async function preWarmCart(): Promise<void> {
        if (!journey.value) {
            return
        }
        if (preWarmAttempted.value) {
            return
        }
        if (!shouldPreWarmOnLoad(journey.value)) {
            return
        }

        preWarmAttempted.value = true
        cart.value = setSegmentLoading(cart.value)
        try {
            const { data, error } = await prepareCartApi(buildContextInput())
            if (error.value) {
                cart.value = resetSegment(cart.value)
                return
            }
            const prepared = data.value ? deserializePrepareCartResponse(data.value) : null
            const nextCartId = prepared ? persistPrepareCartResult(prepared) : null
            if (!nextCartId) {
                cart.value = resetSegment(cart.value)
            }
        } catch {
            cart.value = resetSegment(cart.value)
        }
    }

    /**
     * Fire-and-forget `POST /api/checkout/check-for-credits` on page load when a journey is bound.
     *
     * Requires signed-in session (route rejects anonymous). Failures are **silent** so personalize
     * is not blocked; checkout transition re-runs `checkCredits` before navigation completes.
     */
    async function preWarmEntitlements(): Promise<void> {
        if (!journey.value) {
            return
        }
        if (preWarmCreditsAttempted.value) {
            return
        }
        if (!shouldPreWarmOnLoad(journey.value)) {
            return
        }

        preWarmCreditsAttempted.value = true
        entitlements.value = setSegmentLoading(entitlements.value)
        try {
            const { data, error } = await checkForCreditsApi(buildContextInput())
            if (error.value) {
                entitlements.value = createLoadingSegment(ENTITLEMENTS_SKELETON)
                return
            }

            const payload = entitlementsFromCheckForCredits(
                data.value ? deserializeCheckForCreditsResponse(data.value) : null
            )
            entitlements.value = setSegmentReady(entitlements.value, payload)
            recomputeCheckoutSettlement()
        } catch {
            entitlements.value = createLoadingSegment(ENTITLEMENTS_SKELETON)
        }
    }

    /**
     * Guarantee a cart id exists before submit / background finalize.
     *
     * Returns the existing segment `cartId` without calling prepare-cart when one is already set
     * (pre-warm or a prior ensure). Address updates for that cart are handled separately by
     * `refreshCartAddresses` (after Personalize autosave) and by finalize-cart as a backstop —
     * this helper does **not** re-push billing/shipping when a cart id already exists.
     *
     * When no cart id is present (pre-warm skipped/failed, or guest before email), calls
     * prepare-cart. Throws and sets cart segment `error` if still missing — caller should
     * surface as critical failure.
     * Pass `email` (and other fields) via overrides for guest checkout at submit time.
     */
    async function ensureCartReady(overrides: Partial<CheckoutContextInput> = {}): Promise<string> {
        assertJourneyContextBound()
        if (cart.value.data.cartId?.trim()) {
            return cart.value.data.cartId
        }
        if (cartReadyInFlight) {
            return await cartReadyInFlight
        }

        const generation = checkoutGeneration
        const preparePromise = (async () => {
            cart.value = setSegmentLoading(cart.value)
            const { data, error } = await prepareCartApi(buildContextInput(overrides))
            if (generation !== checkoutGeneration) {
                throw new Error(CHECKOUT_USER_ERROR_MESSAGES.prepareCart)
            }
            if (error.value) {
                const message = CHECKOUT_USER_ERROR_MESSAGES.prepareCart
                cart.value = setSegmentError(cart.value, message)
                throw new Error(message)
            }

            const prepared = data.value ? deserializePrepareCartResponse(data.value) : null
            const nextCartId = prepared ? persistPrepareCartResult(prepared) : null
            if (!nextCartId) {
                cart.value = setSegmentError(cart.value, CHECKOUT_USER_ERROR_MESSAGES.prepareCart)
                throw new Error(CHECKOUT_USER_ERROR_MESSAGES.prepareCart)
            }

            return nextCartId
        })()

        cartReadyInFlight = preparePromise
        try {
            return await preparePromise
        } finally {
            if (cartReadyInFlight === preparePromise) {
                cartReadyInFlight = null
            }
        }
    }

    /**
     * Re-run `POST /api/checkout/prepare-cart` against an already-created cart to push DB addresses.
     *
     * `preWarmCart` often creates the ACCS cart on personalize load, before the recipient/return
     * addresses exist. `handwrittenNoteForm` calls this after a successful autosave that changed
     * recipient/return so the existing cart gets billing/shipping without creating a new cart.
     * `ensureCartReady` / checkout transition do not re-prepare when a cart id already exists;
     * finalize-cart still re-applies addresses as a backstop.
     *
     * Silent/no-op like `preWarmCart` — never throws and keeps the existing cart id on failure.
     * No-ops when no journey or cart exists yet.
     */
    async function refreshCartAddresses(
        overrides: Partial<CheckoutContextInput> = {}
    ): Promise<void> {
        if (!journey.value) {
            return
        }
        if (!cart.value.data.cartId?.trim()) {
            return
        }
        try {
            const { data, error } = await prepareCartApi(buildContextInput(overrides))
            if (error.value) {
                return
            }
            const prepared = data.value ? deserializePrepareCartResponse(data.value) : null
            if (prepared) {
                persistPrepareCartResult(prepared)
            }
        } catch {
            // Silent: keep existing cart id; finalize-cart re-applies addresses before shipping.
        }
    }

    /**
     * `POST /api/checkout/check-for-credits` — DB entitlement read, no ACCS.
     * Updates `entitlements` segment and recomputes `checkoutSettlement` (may pivot to voucher/async).
     */
    /**
     * @param options.quiet - When true and entitlements are already `ready`, do not flip status to
     *   `loading` (submit re-check). Avoids ModeRouter unmounting the credit shell mid-submit.
     */
    async function checkCredits(options?: { quiet?: boolean }): Promise<CheckoutEntitlementsData> {
        assertJourneyContextBound()
        const keepReadyUi = Boolean(options?.quiet && entitlements.value.status === 'ready')
        if (!keepReadyUi) {
            entitlements.value = setSegmentLoading(entitlements.value)
        }
        const { data, error } = await checkForCreditsApi(buildContextInput())
        if (error.value) {
            const message = CHECKOUT_USER_ERROR_MESSAGES.checkCredits
            entitlements.value = setSegmentError(entitlements.value, message)
            throw new Error(message)
        }

        const payload = entitlementsFromCheckForCredits(
            data.value ? deserializeCheckForCreditsResponse(data.value) : null
        )
        entitlements.value = setSegmentReady(entitlements.value, payload)
        recomputeCheckoutSettlement()
        return payload
    }

    /**
     * Mark entitlements `loading` before Send → `/checkout` so DeliveryHero rows and the
     * mode-router CTA skeleton on first paint. Personalize `preWarmEntitlements` may have left
     * them `ready`; without this, checkout flashes ready → loading when `checkCredits` runs.
     */
    function markEntitlementsLoadingForCheckout(): void {
        if (entitlements.value.status !== 'loading') {
            entitlements.value = setSegmentLoading(entitlements.value)
        }
    }

    /**
     * Seed shipping label + personalize addresses from in-memory Pinia (no ACCS round-trip).
     *
     * Same idea as `handwritingProject.pushHandwritingProject` before success: call from Send
     * **before** `navigateTo('/checkout')` so Recipient / Return paint ready on first frame.
     * Hydrate also calls this as a backstop. Prepare/finalize still revalidate (ACCS wins).
     *
     * Prefer shopping-session prepare (`persistPrepareCartResult`) when the API returns
     * addresses. After `/checkout` reload, ready addresses restore from `sessionStorage`
     * (TK-12805) — this seed is the SPA Send → checkout path.
     *
     * Dynamic-imports personalize stores to avoid a Pinia cycle
     * (`handwrittenNoteForm` → `refreshCartAddresses` → this store). Errors propagate;
     * hydrate’s try/catch surfaces them. Unit tests mock these store modules.
     *
     * @returns Whether address cards were set to `ready` from personalize data.
     */
    async function seedOptimisticCheckoutPreview(): Promise<boolean> {
        const optimisticShipping = resolveOptimisticCheckoutShipping(journey.value)
        if (optimisticShipping && shipping.value.status !== 'ready') {
            shipping.value = setSegmentReady(shipping.value, optimisticShipping)
        }

        if (addresses.value.status === 'ready') {
            return true
        }

        const [
            { useHandwrittenNoteFormStore },
            { useContactStore },
            { useReturnAddressStore },
            { useProfileStore },
        ] = await Promise.all([
            import('~~/layers/base/stores/handwrittenNoteForm.store'),
            import('~~/layers/base/stores/contact.store'),
            import('~~/layers/base/stores/returnAddress.store'),
            import('~~/layers/base/stores/profile.store'),
        ])

        const formStore = useHandwrittenNoteFormStore()
        const contactStore = useContactStore()
        const returnAddressStore = useReturnAddressStore()
        const profileStore = useProfileStore()

        const contactId = formStore.selectedContactId
        const addressId = formStore.selectedAddressId
        const contact = contactId ? contactStore.contacts.find((row) => row.id === contactId) : null
        const recipientAddress =
            contact && addressId
                ? (contact.addresses.find((row) => row.id === addressId) ?? null)
                : null
        const returnAddress = returnAddressStore.getAddressById(formStore.selectedReturnAddressId)

        const optimisticAddresses = resolveOptimisticCheckoutAddresses({
            contactName: contact?.name,
            profileNameFallback: profileStore.profileNameFields,
            recipientAddress,
            returnAddress,
        })
        if (optimisticAddresses) {
            addresses.value = setSegmentReady(addresses.value, optimisticAddresses)
            return true
        }
        return false
    }

    /**
     * Load / refresh display addresses from prepare-cart (DB → ACCS).
     *
     * Always attempts prepare when a journey is bound — even if addresses are already `ready`
     * from an optimistic seed — so checkout can double-check personalize Pinia against the
     * server. Silent on failure (finalize still backfills on paid/voucher).
     */
    async function ensureSessionAddressesReady(
        overrides: Partial<CheckoutContextInput> = {}
    ): Promise<void> {
        if (!journey.value) {
            return
        }
        try {
            const { data, error } = await prepareCartApi(buildContextInput(overrides))
            if (error.value) {
                return
            }
            const prepared = data.value ? deserializePrepareCartResponse(data.value) : null
            if (prepared) {
                persistPrepareCartResult(prepared)
            }
        } catch {
            // Finalize still supplies ACCS addresses; keep checkout paint unblocked.
        }
    }

    /**
     * `POST /api/checkout/finalize-cart` — add products, shipping method, fetch cart.
     * Updates `totals`, `shipping`, and `addresses` segments.
     *
     * Keeps optimistic shipping/address previews visible while totals load; ACCS overwrites
     * on success. On failure, totals error; optimistic shipping/addresses stay if already ready.
     */
    async function finalizeCart(): Promise<void> {
        assertJourneyContextBound()
        const generation = checkoutGeneration
        totals.value = setSegmentLoading(totals.value)
        if (shipping.value.status !== 'ready') {
            shipping.value = setSegmentLoading(shipping.value)
        }
        if (addresses.value.status !== 'ready') {
            addresses.value = setSegmentLoading(addresses.value)
        }

        const { data, error } = await finalizeCartApi(buildContextInput())
        if (generation !== checkoutGeneration) {
            return
        }
        if (error.value) {
            const message = CHECKOUT_USER_ERROR_MESSAGES.finalizeCart
            totals.value = setSegmentError(totals.value, message)
            if (shipping.value.status !== 'ready') {
                shipping.value = setSegmentError(shipping.value, message)
            }
            if (addresses.value.status !== 'ready') {
                addresses.value = setSegmentError(addresses.value, message)
            }
            throw new Error(message)
        }

        const finalized = data.value ? deserializeFinalizeCartResponse(data.value) : null
        if (finalized?.cartId?.trim()) {
            persistCartId(finalized.cartId)
        }

        const finalizedSku = skuFromFinalizeCart(finalized)
        if (finalizedSku) {
            placeSku.value = finalizedSku
        }

        totals.value = setSegmentReady(totals.value, totalsFromFinalizeCart(finalized))
        shipping.value = setSegmentReady(shipping.value, shippingFromFinalizeCart(finalized))
        addresses.value = setSegmentReady(addresses.value, addressesFromFinalizeCart(finalized))
    }

    async function prepareAndFinalizeCart(
        overrides: Partial<CheckoutContextInput> = {}
    ): Promise<void> {
        try {
            await ensureCartReady(overrides)
            await finalizeCart()
        } catch (err) {
            const message = toCheckoutUserMessage(err, CHECKOUT_USER_ERROR_MESSAGES.finalizeCart)
            if (totals.value.status === 'loading' || totals.value.status === 'idle') {
                totals.value = setSegmentError(totals.value, message)
            }
            if (shipping.value.status === 'loading') {
                shipping.value = setSegmentError(shipping.value, message)
            }
            if (addresses.value.status === 'loading') {
                addresses.value = setSegmentError(addresses.value, message)
            }
            throw err
        }
    }

    /** Start prepare/finalize without blocking hydrate; coalesce concurrent callers onto one promise. */
    function startFinalizeInBackground(overrides: Partial<CheckoutContextInput> = {}): void {
        if (finalizeInFlight) {
            return
        }
        // Swallow rejection — totals/shipping segments already hold the user-facing error.
        finalizeInFlight = prepareAndFinalizeCart(overrides)
            .catch(() => undefined)
            .finally(() => {
                finalizeInFlight = null
            })
    }

    async function awaitFinalizeInFlight(): Promise<void> {
        if (finalizeInFlight) {
            await finalizeInFlight
        }
    }

    /**
     * `POST /api/checkout/place-cart` — paid sync place (stationery or note), or async_credit enqueue.
     * May set `shoppingSessionId` from response. Only the paid handwritten-note path sends `sku`
     * (server-resolved by finalize); async_credit sends none — no client prepare/finalize runs on
     * that path to source a real one, and the worker resolves + asserts its own authoritative credit
     * SKU server-side rather than trusting a client value (TK-12689 / TK-13047).
     *
     * Does **not** clear journey identity here — `submitOrder` callers still need store IDs for
     * the success URL. Checkout page calls {@link resetCheckout} after resolving that path.
     */
    async function placeOrder(
        paymentMethodCode?: string,
        stripeClientSecret?: string
    ): Promise<void> {
        assertJourneyContextBound()
        const placeOverrides: Partial<CheckoutContextInput> = {}
        // `paymentMethodCode` presence is the paid-vs-credit signal here, not `checkoutSettlement`
        // (which still holds its `async_credit` default until credits resolve, and could be stale
        // for a caller invoking `placeOrder` before that). `submitOrder` only ever passes a code
        // through when settlement is `paid` — never for async_credit — so this stays correct.
        if (journey.value === 'handwritten_note' && paymentMethodCode?.trim()) {
            // Paid only — finalize sets placeSku to the real ACCS SKU. Async credit sends no sku at
            // all (see the store-level doc comment above).
            const sku = placeSku.value?.trim()
            if (!sku) {
                throw new Error(CHECKOUT_USER_ERROR_MESSAGES.placeOrder)
            }
            placeOverrides.sku = sku
        }
        const trimmedClientSecret = stripeClientSecret?.trim()
        const placeInput: PlaceOrderInput = {
            ...buildContextInput(placeOverrides),
            ...(paymentMethodCode?.trim()
                ? {
                      paymentMethod: {
                          code: paymentMethodCode.trim(),
                          ...(trimmedClientSecret
                              ? {
                                    additional_data: [
                                        { key: 'client_secret', value: trimmedClientSecret },
                                    ],
                                }
                              : {}),
                      },
                  }
                : {}),
        }
        const { data, error } = await placeOrderApi(placeInput)
        if (error.value) {
            throw new Error(CHECKOUT_USER_ERROR_MESSAGES.placeOrder)
        }
        const placed = data.value ? deserializePlaceOrderResponse(data.value) : null
        const sessionId = placed?.shoppingSessionId
        if (sessionId?.trim()) {
            shoppingSessionId.value = sessionId.trim()
        }
    }

    /** Apply promo code on checkout page (route may 404 until backend lands). */
    async function applyCoupon(code: string): Promise<void> {
        assertJourneyContextBound()
        coupon.value = setSegmentLoading(coupon.value)
        const trimmed = code.trim()
        const { data, error } = await applyCouponApi({
            ...buildContextInput(),
            couponCode: trimmed,
        })
        if (error.value) {
            const message = CHECKOUT_USER_ERROR_MESSAGES.applyCoupon
            coupon.value = setSegmentError(coupon.value, message)
            throw new Error(message)
        }
        coupon.value = setSegmentReady(coupon.value, {
            code: data.value?.code ?? trimmed,
            applied: true,
        })
    }

    /** Remove applied coupon from cart (route may 404 until backend lands). */
    async function removeCoupon(): Promise<void> {
        assertJourneyContextBound()
        coupon.value = setSegmentLoading(coupon.value)
        const { error } = await removeCouponApi(buildContextInput())
        if (error.value) {
            const message = CHECKOUT_USER_ERROR_MESSAGES.removeCoupon
            coupon.value = setSegmentError(coupon.value, message)
            throw new Error(message)
        }
        coupon.value = setSegmentReady(coupon.value, { code: null, applied: false })
    }

    /**
     * Re-sync the current default/selected return address onto an already-existing ACCS cart
     * (TK-12318). `prepareGuestCart` only pushes addresses at cart-creation time — call this after
     * the return address changes post pre-warm (e.g. after saving an edited return address).
     *
     * Silent/no-op like `preWarmCart` — never throws. No-ops when no journey is bound or no cart
     * exists yet (nothing to sync; the next prepare-cart call will pick up the fresh address).
     * Pass `returnAddressId` via overrides to target one specific address; omit to let the server
     * re-run its usual default-address resolution.
     */
    async function syncReturnAddressToCart(
        overrides: Partial<CheckoutContextInput> = {}
    ): Promise<CheckoutReturnAddressSyncResult> {
        if (!journey.value) {
            return { ok: false, skipped: 'no_journey' }
        }
        const activeCartId = cart.value.data.cartId?.trim()
        if (!activeCartId) {
            return { ok: false, skipped: 'no_active_cart' }
        }

        try {
            const { data, error } = await syncReturnAddressApi(buildContextInput(overrides))
            if (error.value) {
                return {
                    ok: false,
                    error: toCheckoutUserMessage(
                        error.value,
                        CHECKOUT_USER_ERROR_MESSAGES.syncReturnAddress
                    ),
                }
            }
            const synced = data.value ? deserializeSyncReturnAddressResponse(data.value) : null
            const cartId = synced?.cartId?.trim() || activeCartId
            return { ok: true, cartId }
        } catch (caught) {
            return {
                ok: false,
                error: toCheckoutUserMessage(
                    caught,
                    CHECKOUT_USER_ERROR_MESSAGES.syncReturnAddress
                ),
            }
        }
    }

    /**
     * `POST /api/checkout/get-payment-intent` — requests a Stripe `client_secret` from ACCS's
     * Stripe module for the active cart. Call right before `stripe.confirmPayment` on the
     * paid-settlement submit path (TK-12183 follow-on).
     */
    async function getPaymentIntent(): Promise<
        { ok: true; clientSecret: string } | { ok: false; error: string }
    > {
        const activeCartId = cart.value.data.cartId?.trim()
        if (!activeCartId) {
            return { ok: false, error: CHECKOUT_USER_ERROR_MESSAGES.getPaymentIntent }
        }

        try {
            const { data, error } = await getPaymentIntentApi(buildContextInput())
            if (error.value) {
                return {
                    ok: false,
                    error: toCheckoutUserMessage(
                        error.value,
                        CHECKOUT_USER_ERROR_MESSAGES.getPaymentIntent
                    ),
                }
            }
            const deserialized = data.value ? deserializeGetPaymentIntentResponse(data.value) : null
            const clientSecret = deserialized?.clientSecret?.trim()
            if (!clientSecret) {
                return { ok: false, error: CHECKOUT_USER_ERROR_MESSAGES.getPaymentIntent }
            }
            return { ok: true, clientSecret }
        } catch (caught) {
            return {
                ok: false,
                error: toCheckoutUserMessage(caught, CHECKOUT_USER_ERROR_MESSAGES.getPaymentIntent),
            }
        }
    }

    /**
     * Persists Stripe billing Address Element value to profile return addresses, then syncs the cart
     * (billing always; ACCS shipping only when journey maps return → shipping).
     *
     * Formerly `persistStripeDelivery` — renamed because “delivery/shipping” here means recipient
     * address elsewhere in checkout; this call is payer billing only. Invoke at submit via
     * `getValue()`, not on every change.
     *
     * Tax uses ACCS cart **shipping**: notes keep recipient shipping; stationery also syncs this
     * billing value onto shipping, so tax follows that address after persist.
     */
    async function persistStripeBillingAddress(args: {
        billingAddress: CheckoutCartAddress
        isLoggedIn: boolean
    }): Promise<PersistStripeBillingAddressResult> {
        const { doCreateAddress } = useAddressService()

        return runPersistStripeBillingAddress(
            {
                billingAddress: args.billingAddress,
                journey: journey.value,
                isLoggedIn: args.isLoggedIn,
                activeCartId: cart.value.data.cartId,
            },
            {
                createAddress: async (payload) => {
                    const { data, error } = await doCreateAddress(payload)
                    if (error.value) {
                        return {
                            addressId: null,
                            error: toCheckoutUserMessage(
                                error.value,
                                CHECKOUT_USER_ERROR_MESSAGES.persistStripeBillingAddress
                            ),
                        }
                    }
                    const addressId = data.value?.address?.id?.trim() ?? null
                    return {
                        addressId,
                        error: addressId
                            ? null
                            : CHECKOUT_USER_ERROR_MESSAGES.persistStripeBillingAddress,
                    }
                },
                syncReturnAddressToCart: ({ returnAddressId }) =>
                    syncReturnAddressToCart({ returnAddressId }),
            }
        )
    }

    /**
     * Personalize Send → checkout navigation.
     *
     * Runs a fast entitlement check, then returns so the page can navigate immediately.
     * Signed-in paid paths may kick off cart prep in the background — `hydrateCheckoutPage` completes it.
     * Guests defer prepare-cart until checkout supplies email.
     */
    async function beginCheckoutTransition(
        options: CheckoutPipelineAuthContext
    ): Promise<CheckoutTransitionResult> {
        if (!journey.value) {
            return {
                ok: false,
                error: CHECKOUT_USER_ERROR_MESSAGES.journeyNotBound,
            }
        }

        return withLoadingScope('transition', async () => {
            try {
                const entitlementSnapshot = await checkCredits()
                const pivot = resolveAsyncCreditPivot(entitlementSnapshot)
                if (pivot) {
                    return pivot
                }

                if (
                    options.isLoggedIn &&
                    checkoutSettlement.value === 'paid' &&
                    !cart.value.data.cartId?.trim()
                ) {
                    void ensureCartReady().catch(() => {
                        // hydrateCheckoutPage retries; silent here per pre-warm pattern
                    })
                }

                return { ok: true, settlement: checkoutSettlement.value }
            } catch (err) {
                return {
                    ok: false,
                    error: toCheckoutUserMessage(err, CHECKOUT_USER_ERROR_MESSAGES.checkCredits),
                }
            }
        })
    }

    /**
     * Checkout page mount — seed optimistic shipping/addresses, return so the page can paint,
     * then run prepare-cart (addresses) + finalize in the background for **paid / voucher**
     * (TK-12688).
     *
     * Does **not** await prepare-cart before resolving — Send → `/checkout` must not feel blocked
     * on ACCS cart prep. Prefer {@link seedOptimisticCheckoutPreview} **before** navigate from
     * personalize so address cards are already `ready` on first paint; this hydrate call is the
     * backstop. Segment loading covers money/shipping until background work finishes.
     *
     * Guests without email return `awaitingCheckoutEmail` so Stripe can collect email first.
     * Call again with `email` once checkout UI has it. Async credit: optimistic seed only —
     * prepare + finalize live in the queue worker after place-cart (TK-12689). Paid/voucher
     * prepare still runs after paint even when addresses are already seeded (double-check).
     * Ready addresses also restore from `sessionStorage` on reload (TK-12805).
     */
    async function hydrateCheckoutPage(
        options: HydrateCheckoutPageOptions
    ): Promise<CheckoutHydrateResult> {
        if (!journey.value) {
            return {
                ok: false,
                error: CHECKOUT_USER_ERROR_MESSAGES.journeyNotBound,
            }
        }

        const hydrateResult = await withLoadingScope('hydrate', async () => {
            try {
                if (
                    isAwaitingGuestCheckoutEmail(
                        options.isLoggedIn,
                        options.email,
                        cart.value.data.cartId
                    )
                ) {
                    return {
                        ok: true,
                        settlement: checkoutSettlement.value,
                        awaitingCheckoutEmail: true,
                    } satisfies CheckoutHydrateResult
                }

                await seedOptimisticCheckoutPreview()
                // Keep skeletons until seed (or later prepare/finalize) marks addresses ready.
                // Async credit: no client prepare/finalize (TK-12689). If seed failed, leave
                // `error` instead of `loading` so Recipient/Return are not stuck and Send is not
                // blocked — worker still uses handwriting-project address FKs.
                if (addresses.value.status !== 'ready') {
                    if (checkoutSettlement.value === 'async_credit') {
                        addresses.value = setSegmentError(
                            addresses.value,
                            CHECKOUT_USER_ERROR_MESSAGES.addressPreviewUnavailable
                        )
                    } else {
                        addresses.value = setSegmentLoading(addresses.value)
                    }
                }

                return {
                    ok: true,
                    cartId: cart.value.data.cartId ?? undefined,
                    settlement: checkoutSettlement.value,
                } satisfies CheckoutHydrateResult
            } catch (err) {
                const message = toCheckoutUserMessage(
                    err,
                    CHECKOUT_USER_ERROR_MESSAGES.finalizeCart
                )
                return {
                    ok: false,
                    error: message,
                    critical: !cart.value.data.cartId,
                } satisfies CheckoutHydrateResult
            }
        })

        if (
            hydrateResult.ok &&
            !hydrateResult.awaitingCheckoutEmail &&
            checkoutSettlement.value !== 'async_credit'
        ) {
            const overrides = prepareCartOverrides(options.isLoggedIn, options.email)
            // Prepare refreshes addresses even after optimistic seed (double-check), then finalize.
            void ensureSessionAddressesReady(overrides).finally(() => {
                startFinalizeInBackground(overrides)
            })
        }

        return hydrateResult
    }

    /**
     * Checkout Submit → processing scrim.
     *
     * Paid / voucher: finalize (if needed) + `place-cart`.
     * Async credit: `place-cart` only (consume + enqueue; prepare/finalize in worker).
     */
    async function submitOrder(options?: {
        paymentMethodCode?: string
        /** Stripe PaymentIntent `client_secret` for ACCS `additional_data` (TK-12183). */
        stripeClientSecret?: string
    }): Promise<CheckoutOrderSubmitResult> {
        if (!journey.value) {
            return {
                ok: false,
                error: CHECKOUT_USER_ERROR_MESSAGES.journeyNotBound,
            }
        }

        return withLoadingScope('submit', async () => {
            try {
                const wasCreditCheckout = checkoutSettlement.value === 'async_credit'
                const hasPaymentMethod = Boolean(options?.paymentMethodCode?.trim())

                // Credit→paid revoke pivot only. Paid submit after Stripe already confirmed —
                // skip; place-cart re-resolves settlement server-side.
                if (journey.value === 'handwritten_note' && !hasPaymentMethod) {
                    // Quiet when already ready so ModeRouter does not unmount the credit shell
                    // (that dropped `emit('submitted')` and left users on Confirm & Send).
                    await checkCredits({ quiet: true })
                }

                const paidPivot = resolvePaidCheckoutPivot(wasCreditCheckout, options)
                if (paidPivot) {
                    return paidPivot
                }

                // async_credit: no client prepare/finalize — worker owns ACCS and resolves its own
                // credit SKU server-side, so there's nothing to await/finalize here (TK-12689 / TK-13047).
                if (checkoutSettlement.value !== 'async_credit') {
                    await awaitFinalizeInFlight()

                    if (totals.value.status === 'error') {
                        return {
                            ok: false,
                            error: totals.value.error || CHECKOUT_USER_ERROR_MESSAGES.finalizeCart,
                        }
                    }

                    if (totals.value.status !== 'ready' || shipping.value.status !== 'ready') {
                        await ensureCartReady()
                        await finalizeCart()
                    }
                }

                if (shouldRunPlaceOrder(checkoutSettlement.value)) {
                    const paymentMethodCode =
                        checkoutSettlement.value === 'paid' ? options?.paymentMethodCode : undefined
                    await placeOrder(paymentMethodCode, options?.stripeClientSecret)
                }

                // Page watches this — do not rely on shell `emit('submitted')` after place.
                pendingCheckoutSuccessNavigation.value = true

                return {
                    ok: true,
                    settlement: checkoutSettlement.value,
                    shoppingSessionId: shoppingSessionId.value,
                }
            } catch (err) {
                return {
                    ok: false,
                    error: toCheckoutUserMessage(err, CHECKOUT_USER_ERROR_MESSAGES.placeOrder),
                    critical: !cart.value.data.cartId,
                }
            }
        })
    }

    /**
     * @deprecated Prefer phased pipeline: `beginCheckoutTransition` → `hydrateCheckoutPage` → `submitOrder`.
     *
     * End-to-end pipeline for legacy callers or integration tests.
     */
    async function submitCheckout(
        options: HydrateCheckoutPageOptions
    ): Promise<CheckoutSubmitResult> {
        const transition = await beginCheckoutTransition(options)
        if (!transition.ok) {
            if ('pivoted' in transition) {
                return { ok: false, pivoted: 'async_credit' }
            }
            return { ok: false, error: transition.error }
        }

        const hydrate = await hydrateCheckoutPage(options)
        if (!hydrate.ok) {
            return {
                ok: false,
                error: hydrate.error,
                critical: hydrate.critical,
            }
        }

        if (hydrate.awaitingCheckoutEmail) {
            return {
                ok: false,
                error: CHECKOUT_USER_ERROR_MESSAGES.checkoutEmailRequired,
            }
        }

        const submit = await submitOrder()
        if (!submit.ok) {
            if ('pivoted' in submit) {
                return { ok: false, pivoted: 'async_credit' }
            }
            return {
                ok: false,
                error: submit.error,
                critical: submit.critical,
            }
        }

        return {
            ok: true,
            cartId: cart.value.data.cartId!,
            settlement: submit.settlement,
            shoppingSessionId: submit.shoppingSessionId,
        }
    }

    /**
     * Clears checkout segments and unbinds journey identity after place success, abandon, or logout.
     *
     * Must null journey/entity/`shoppingSessionId` too — not just segments — so an in-progress
     * attempt cannot survive into the next session on this tab. Also clears `sessionStorage`
     * persist; if `journey` stayed set, `$subscribe` would rewrite it.
     *
     * Call from the checkout page **after** resolving the success URL (those paths read store IDs).
     * Also called when leaving the checkout-active zone (lifecycle plugin) or when
     * {@link bindJourneyContext} binds a different journey/project (TK-12805).
     */
    function resetCheckout(): void {
        stopLoadingScope()
        checkoutGeneration += 1
        finalizeInFlight = null
        cartReadyInFlight = null
        placeSku.value = null
        cart.value = resetSegment(cart.value)
        // Entitlements use loading (not idle) so DeliveryHero / mode-router CTA start as skeletons
        // and do not flash ready → loading when check-for-credits re-runs after personalize pre-warm.
        entitlements.value = createLoadingSegment(ENTITLEMENTS_SKELETON)
        totals.value = resetSegment(totals.value)
        shipping.value = resetSegment(shipping.value)
        // Addresses use loading (not idle) so Recipient / Return never paint empty labels.
        addresses.value = createLoadingSegment(ADDRESSES_SKELETON)
        coupon.value = resetSegment(coupon.value)
        journey.value = null
        designProjectId.value = null
        handwritingProjectId.value = null
        handwritingStationeryId.value = null
        shoppingSessionId.value = null
        checkoutSettlement.value = DEFAULT_CHECKOUT_SETTLEMENT
        stripeBillingAddressComplete.value = false
        stripePaymentComplete.value = false
        stripeClientSecret.value = null
        pendingPaidCheckoutPivot.value = false
        pendingCheckoutSuccessNavigation.value = false
        preWarmAttempted.value = false
        preWarmCreditsAttempted.value = false
        clearShoppingSessionPersist()
    }

    return {
        designProjectId,
        handwritingProjectId,
        handwritingStationeryId,
        journey,
        shoppingSessionId,
        checkoutSettlement,
        stripeBillingAddressComplete,
        stripePaymentComplete,
        stripeClientSecret,
        checkoutMode,
        pendingPaidCheckoutPivot,
        pendingCheckoutSuccessNavigation,
        loadingPhase,
        activeLoadingScope,
        isCheckoutSlow,
        showCheckoutEngagedContent,
        cart,
        entitlements,
        totals,
        shipping,
        addresses,
        coupon,
        cartId,
        isCartSegmentLoading,
        isOrchestrationEnabled,
        bindJourneyContext,
        preWarmCart,
        preWarmEntitlements,
        ensureCartReady,
        refreshCartAddresses,
        checkCredits,
        finalizeCart,
        placeOrder,
        applyCoupon,
        removeCoupon,
        syncReturnAddressToCart,
        getPaymentIntent,
        persistStripeBillingAddress,
        beginCheckoutTransition,
        hydrateCheckoutPage,
        /** Call from personalize Send before navigate so checkout address cards paint ready. */
        seedOptimisticCheckoutPreview,
        /** Call from personalize Send before navigate so DeliveryHero / CTA start loading. */
        markEntitlementsLoadingForCheckout,
        submitOrder,
        submitCheckout,
        clearPendingPaidCheckoutPivot,
        clearPendingCheckoutSuccessNavigation,
        resetCheckout,
    }
})
